import {
  DocumentMatchMethod,
  DocumentVerificationStatus,
  VerificationDocumentOutcome,
  VerificationFieldKey,
  VerificationTargetEntity,
  VerificationVerdict,
  VerifiedDocumentType,
} from 'typescript/enums/documentVerification'
import {
  IClientDocumentVerification,
  IDocumentMatch,
  IFieldVerification,
  IOwnerVerificationSummary,
  IVerificationNotice,
  IVerifiedDocument,
  VerificationSeverity,
} from 'typescript/interfaces/documentVerification'
import { convertToPercentageFormat, convertToPriceFormat } from 'utils/formatters'

export const VERIFICATION_UNAVAILABLE_MESSAGE = 'Verification unavailable right now. Document uploaded, but not checked against system data.'

const MATCH_MESSAGE = 'Matches the uploaded document'
const NOT_FOUND_MESSAGE = 'Not found in uploaded document. Please verify manually.'
const EMPTY_VALUE = 'n/a'
const VISIBLE_DIGITS = 4
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

const verifiedDocumentTypes: string[] = Object.values(VerifiedDocumentType)
const fieldKeyOrder: string[] = Object.values(VerificationFieldKey)

const documentTypeLabels: Record<VerifiedDocumentType, string> = {
  [VerifiedDocumentType.voidedCheck]: 'Voided Check',
  [VerifiedDocumentType.application]: 'Application',
  [VerifiedDocumentType.driverLicense]: "Driver's License",
  [VerifiedDocumentType.w9]: 'W9',
}

const ownerFieldLabels: Partial<Record<VerificationFieldKey, string>> = {
  [VerificationFieldKey.ownerFirstName]: 'First Name',
  [VerificationFieldKey.ownerLastName]: 'Last Name',
  [VerificationFieldKey.ownerBirthDate]: 'Date Of Birth',
  [VerificationFieldKey.ownerOwnership]: 'Ownership %',
  [VerificationFieldKey.ownerSsn]: 'SSN',
  [VerificationFieldKey.ownerDriverLicense]: "Driver's License #",
  [VerificationFieldKey.ownerDriverLicenseExpiration]: 'DL Exp Date',
  [VerificationFieldKey.ownerDriverLicenseState]: 'State Issued DL',
  [VerificationFieldKey.ownerStreet]: 'Home Address',
  [VerificationFieldKey.ownerCity]: 'City',
  [VerificationFieldKey.ownerState]: 'State',
  [VerificationFieldKey.ownerZipCode]: 'Zip Code',
  [VerificationFieldKey.ownerEmail]: 'Email',
  [VerificationFieldKey.ownerMobile]: 'Mobile',
}

const dateFieldKeys = [VerificationFieldKey.businessStartDate, VerificationFieldKey.ownerBirthDate, VerificationFieldKey.ownerDriverLicenseExpiration]

const verdictSeverities: Record<VerificationVerdict, VerificationSeverity> = {
  [VerificationVerdict.match]: 'success',
  [VerificationVerdict.unverifiable]: 'warning',
  [VerificationVerdict.documentOnly]: 'warning',
  [VerificationVerdict.mismatch]: 'error',
  [VerificationVerdict.conflict]: 'error',
}

const severityRanks: Record<VerificationSeverity, number> = {
  success: 0,
  info: 1,
  warning: 2,
  error: 3,
}

const uncheckedOutcomes = [
  VerificationDocumentOutcome.serviceUnavailable,
  VerificationDocumentOutcome.timeout,
  VerificationDocumentOutcome.invalidResponse,
  VerificationDocumentOutcome.documentUnavailable,
  VerificationDocumentOutcome.notExtracted,
]

const unsupportedOutcomeReasons: Partial<Record<VerificationDocumentOutcome, string>> = {
  [VerificationDocumentOutcome.unsupportedFormat]: 'unsupported file format',
  [VerificationDocumentOutcome.documentTooLarge]: 'file too large',
}

const unique = (values: string[]) => values.filter((value, index) => values.indexOf(value) === index)

const joinWithAnd = (values: string[]) => (values.length > 1 ? `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}` : values.join(''))

const displayValue = (value: string | null) => value || EMPTY_VALUE

const lastVisibleDigits = (value: string | null) => (value ?? '').replace(/\D/g, '').slice(-VISIBLE_DIGITS)

const getDocumentLabel = (documents: IVerifiedDocument[], documentId: number) => {
  const document = documents.find(item => item.clientDocumentId === documentId)
  return document ? documentTypeLabels[document.documentType] : 'Document'
}

const getMatchedDocumentLabel = (match: IDocumentMatch) =>
  match.ownerSlot ? `${documentTypeLabels[match.documentType]} (owner ${match.ownerSlot})` : documentTypeLabels[match.documentType]

const formatSystemValue = (fieldKey: VerificationFieldKey, value: string | null) => {
  if (!value) return EMPTY_VALUE
  if (fieldKey === VerificationFieldKey.grossAnnualIncome) return convertToPriceFormat(Number(value))
  if (fieldKey === VerificationFieldKey.ownerOwnership) return convertToPercentageFormat(value)

  const dateParts = dateFieldKeys.includes(fieldKey) ? ISO_DATE_PATTERN.exec(value) : null
  if (!dateParts) return value

  const [, year, month, day] = dateParts
  return `${month}.${day}.${year}`
}

const describeMismatch = (field: IFieldVerification) =>
  field.differsOnlyInHiddenDigits
    ? `Mismatch: document and system values both end in ${lastVisibleDigits(field.systemValue)} but differ in the hidden digits`
    : `Mismatch: document shows ${displayValue(field.extractedValue)}, system has ${formatSystemValue(field.fieldKey, field.systemValue)}`

const describeConflict = (field: IFieldVerification, documents: IVerifiedDocument[]) => {
  if (field.differsOnlyInHiddenDigits) {
    const documentLabels = field.conflictingValues.map(value => getDocumentLabel(documents, value.documentId))
    const digits = lastVisibleDigits(field.conflictingValues[0].value)
    return `Conflicting values found: the ${joinWithAnd(documentLabels)} values end in ${digits} but differ in the hidden digits`
  }

  const values = field.conflictingValues.map(value => `${getDocumentLabel(documents, value.documentId)}: ${displayValue(value.value)}`)
  return `Conflicting values found: ${values.join(' vs ')}`
}

const verdictMessages: Record<VerificationVerdict, (field: IFieldVerification, documents: IVerifiedDocument[]) => string> = {
  [VerificationVerdict.match]: () => MATCH_MESSAGE,
  [VerificationVerdict.mismatch]: describeMismatch,
  [VerificationVerdict.unverifiable]: () => NOT_FOUND_MESSAGE,
  [VerificationVerdict.documentOnly]: field =>
    `Document shows ${displayValue(field.extractedValue)} - this field is empty in the system. Please fill the field if needed.`,
  [VerificationVerdict.conflict]: describeConflict,
}

const describeUnmatchedDocument = (match: IDocumentMatch) => {
  const target = match.documentType === VerifiedDocumentType.voidedCheck ? 'bank account' : 'owner'
  return `${getMatchedDocumentLabel(match)} doesn't match any ${target} of this client. Please verify manually.`
}

export const isVerifiedDocumentType = (documentType: string) => verifiedDocumentTypes.includes(documentType)

export const getVerdictSeverity = (verdict: VerificationVerdict) => verdictSeverities[verdict]

export const findFieldVerification = (
  verification: IClientDocumentVerification | undefined,
  targetEntity: VerificationTargetEntity,
  targetEntityId: number | string | undefined,
  fieldKey: VerificationFieldKey
) => {
  if (!verification || targetEntityId === undefined) return undefined

  return verification.fields.find(
    field => field.targetEntity === targetEntity && field.targetEntityId === Number(targetEntityId) && field.fieldKey === fieldKey
  )
}

export const getFieldVerificationMessage = (field: IFieldVerification, documents: IVerifiedDocument[]) =>
  verdictMessages[field.verdict](field, documents)

export const getFieldVerificationSource = (field: IFieldVerification, documents: IVerifiedDocument[]) => {
  if (!field.sourceDocumentId) return null

  const page = field.page ? `, page ${field.page}` : ''
  const sourceText = field.sourceText ? `: "${field.sourceText}"` : ''
  return `${getDocumentLabel(documents, field.sourceDocumentId)}${page}${sourceText}`
}

export const getOwnerVerificationSummary = (verification: IClientDocumentVerification, ownerId: number): IOwnerVerificationSummary | undefined => {
  const ownerFields = verification.fields
    .filter(field => field.targetEntity === VerificationTargetEntity.owner && field.targetEntityId === ownerId)
    .sort((first, second) => fieldKeyOrder.indexOf(first.fieldKey) - fieldKeyOrder.indexOf(second.fieldKey))

  if (!ownerFields.length) return undefined

  const findings = ownerFields
    .filter(field => field.verdict !== VerificationVerdict.match)
    .map(field => ({
      severity: getVerdictSeverity(field.verdict),
      label: ownerFieldLabels[field.fieldKey] ?? field.fieldKey,
      message: getFieldVerificationMessage(field, verification.documents),
    }))
    .sort((first, second) => severityRanks[second.severity] - severityRanks[first.severity])

  const checkedAgainst = verification.documentMatches
    .filter(match => match.targetEntity === VerificationTargetEntity.owner && match.targetEntityId === ownerId)
    .map(getMatchedDocumentLabel)

  return {
    severity: findings.length ? findings[0].severity : 'success',
    checkedAgainst: unique(checkedAgainst),
    findings,
    matchedFields: ownerFields
      .filter(field => field.verdict === VerificationVerdict.match)
      .map(field => ownerFieldLabels[field.fieldKey] ?? field.fieldKey),
  }
}

export const getVerificationNotices = (verification: IClientDocumentVerification) => {
  const notices: IVerificationNotice[] = []
  const uncheckedLabels = unique(
    verification.documents.filter(document => uncheckedOutcomes.includes(document.outcome)).map(document => documentTypeLabels[document.documentType])
  )
  const unsupportedLabels = unique(
    verification.documents
      .filter(document => unsupportedOutcomeReasons[document.outcome])
      .map(document => `${documentTypeLabels[document.documentType]} (${unsupportedOutcomeReasons[document.outcome]})`)
  )
  const unmatchedMessages = unique(
    verification.documentMatches.filter(match => match.method === DocumentMatchMethod.unmatched).map(describeUnmatchedDocument)
  )

  if (verification.status === DocumentVerificationStatus.unavailable || uncheckedLabels.length) {
    notices.push({
      severity: 'info',
      message: uncheckedLabels.length
        ? `${VERIFICATION_UNAVAILABLE_MESSAGE} Not checked: ${uncheckedLabels.join(', ')}.`
        : VERIFICATION_UNAVAILABLE_MESSAGE,
    })
  }

  if (unsupportedLabels.length) {
    notices.push({
      severity: 'info',
      message: `These files can't be checked automatically. Please verify them manually: ${unsupportedLabels.join(', ')}.`,
    })
  }

  unmatchedMessages.forEach(message => notices.push({ severity: 'warning', message }))

  return notices
}
